import {makeAutoObservable, observable, toJS} from 'mobx';

import type {Nullable} from '../utility-types';

export class ListManager<T> {
  entries: T[];

  constructor(initial?: T[]) {
    this.entries = initial ?? [];

    makeAutoObservable(
      this,
      {
        entries: observable.shallow,
      },
      {
        autoBind: true,
      },
    );
  }

  get isEmpty() {
    return this.entries.length === 0;
  }

  get isNotEmpty() {
    return this.entries.length > 0;
  }

  get length() {
    return this.entries.length;
  }

  add(value: T) {
    this.entries.push(value);

    return this;
  }

  addAndOverride(value: T) {
    if (this.entries.includes(value)) {
      this.delete(value);
    } else {
      this.add(value);
    }

    return this;
  }

  addMany(entries: T[]) {
    this.entries.push(...entries.slice());

    return this;
  }

  clear() {
    this.entries = [];

    return this;
  }

  delete(value: T) {
    const index = this.entries.indexOf(value);

    if (index !== -1) {
      this.entries.splice(index, 1);
    }

    return this;
  }

  filter(predicate: (value: T, index: number, array: T[]) => boolean): T[] {
    return this.entries.filter(predicate);
  }

  find(
    predicate: (value: T, index: number, array: T[]) => boolean,
  ): Nullable<T> {
    return this.entries.filter(predicate)[0] ?? null;
  }

  has(item: T) {
    return this.entries.includes(item);
  }

  resetEntries(nextEntries: T[]) {
    this.clear().addMany(nextEntries);
  }

  toJS() {
    return toJS(this.entries);
  }

  update(value: T, newValue: T) {
    const index = this.entries.indexOf(value);

    if (index !== -1) {
      this.entries[index] = {...this.entries[index], ...newValue};
    }

    return this;
  }
}
